import React from 'react';
import { Box, Typography, LinearProgress, Chip, Alert } from '@mui/material';

type BatchStatusIndicatorProps = {
  batchProgress: {
    completed: number;
    total: number;
    isComplete: boolean;
  };
  storeCount: number;
  isLoading: boolean;
  isFetching: boolean;
  error?: any;
};

/**
 * Component to display batch processing status
 * Shows progress of batch requests and provides visual feedback to users
 */
const BatchStatusIndicator: React.FC<BatchStatusIndicatorProps> = ({
  batchProgress,
  storeCount,
  isLoading,
  isFetching,
  error,
}) => {
  // Calculate progress percentage
  const progressPercentage =
    batchProgress.total > 0
      ? (batchProgress.completed / batchProgress.total) * 100
      : 0;

  // If there's an error, show error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error processing batches: {error.message || 'Unknown error'}
      </Alert>
    );
  }

  // If loading or fetching, show progress
  if (isLoading || isFetching) {
    return (
      <Box sx={{ mb: 3, width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Processing {storeCount} stores in batches of 100
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {batchProgress.completed} of {batchProgress.total} batches
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progressPercentage}
          sx={{ height: 8, borderRadius: 4 }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Chip
            label={`${Math.round(progressPercentage)}% complete`}
            color="primary"
            variant="outlined"
          />
        </Box>
      </Box>
    );
  }

  // If complete, show success state
  if (batchProgress.isComplete && batchProgress.total > 0) {
    return (
      <Alert severity="success" sx={{ mb: 2 }}>
        Successfully processed {storeCount} stores in {batchProgress.total}{' '}
        batches
      </Alert>
    );
  }

  // Default state (not started)
  return null;
};

export default BatchStatusIndicator;
